import { Commit, Dispatch, StoreOptions } from 'vuex';
import dayjs from 'dayjs';
import { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import meeting from '@/renderer/scripts/utils/meeting';
import tips from '@/renderer/scripts/utils/tips';
import user from '@/renderer/scripts/utils/user';
import { httpUrl, CONSTANT_URL } from '@/renderer/scripts/http';
import { IS_PRIVATE } from '@/common/constant';

interface SpaceInfo {
  capacity: string;
  availableSpace: string;
  totalAllocatedSpaceQuota: string;
}
interface UserCountInfo {
  total: number;
  used: number;
  rest: number;
  temporaryUser: number;
  online: number;
  disabledTemporaryUser: number;
  disabledFormalUser: number;
}

interface CorpItem {
  name: string;
  corpId: string;
}

export interface OrgState {
  bussiness: string; // 行业信息
  orgName: string; // 组织名
  orgLogo: string; // 组织机构logo
  showOrgName: boolean; // 是否显示组织名称
  space: string; // 总配额
  enableManageWatermark: boolean; // 是否能管理水印设置
  previewWatermarkConfig: CoFile.PreviewWatermarkConfig; // 预览水印
  downloadWatermarkConfig: CoFile.DownloadWatermarkConfig; // 下载水印
  shareWatermarkConfig: CoFile.ShareWatermarkConfig; // 分享水印
  enableShare: boolean; // 企业内是否允许共享
  enableWeworkLogin: boolean;
  enableOpenLdapLogin?: boolean;
  enableWindowsAdLogin?: boolean;
  enableYufuLogin: boolean;
  enableSensitiveWord: boolean; // 企业是否有自定义敏感词模块
  enableGlobalFullTextSearch: boolean; // 是否显示全局搜索模块-取决于扫描
  enableManageAntivirus: boolean; // 企业是否有安全扫描模块
  allowChangeNickname: boolean; // 是否允许修改昵称
  enableViewAllOrgUser: boolean; // 是否显示企业的组织架构（隐藏后只能看到自己加入的团队和组织，只能精确搜索）
  ssoWay: string;
  syncWay: string;
  showWeworkAppLink: boolean;
  defaultUserOptions: SMH.DefaultUserOptions;
  defaultTeamOptions: SMH.DefaultTeamOptions;
  expireTime: string;
  totalFileSize: string;
  remainingSpaceQuota: string;
  teamSpaceQuota: string; // 每个空间的可用额度
  groupSpaceQuota: string; // 每个群组的可用额度
  domains: SMH.DomainItem[];
  allowWeworkSelfApp: boolean;
  enableAntivirus: boolean; // 当前企业是否 开启云查杀
  defaultLogo: string;
  userCountInfo: UserCountInfo;
  instanceId: string;
  supportProtocols: string[];
  isAccountNotDependentOnPhone: boolean; // 是否允许不绑定手机号
  corpList: CorpItem[];
}
const initUserOptions = {
  enabled: true,
  allowPersonalSpace: false,
  // FIX: 默认不给大小，团队版无默认值时使用共享额度
  personalSpaceQuotaSize: '',
};
const initTeamOptions = {
  defaultRoleId: 0,
  // 默认团队空间为共享
  spaceQuotaSize: '',
};
const previewWatermarkOptions = {
  enablePreviewWatermark: true,
  previewWatermarkType: 0,
};
const downloadWatermarkOptions = {
  enableDownloadWatermark: true,
  downloadWatermarkType: 0,
};
const shareWatermarkOptions = {
  enableShareWatermark: true,
  shareWatermarkType: 1,
};
const defaultOrg = {
  enableAntivirus: true,
};
const defaultLogo = meeting.isTMWebview
  ? httpUrl.staticCDNURLMap.productWemeetLogoUrl
  : httpUrl.staticCDNURLMap.productLogoUrl;
const org: StoreOptions<OrgState> = {
  state: {
    bussiness: '', // 默认其他行业
    orgName: meeting.isTMWebview ? i18n_t('会议文件空间') : i18n_t('腾讯云企业网盘'),
    orgLogo: defaultLogo,
    enableAntivirus: defaultOrg.enableAntivirus,
    showOrgName: false,
    space: '0',
    // 是否能管理水印设置
    enableManageWatermark: true,
    previewWatermarkConfig: previewWatermarkOptions,
    downloadWatermarkConfig: downloadWatermarkOptions,
    shareWatermarkConfig: shareWatermarkOptions,
    enableShare: false,
    enableWeworkLogin: false,
    enableOpenLdapLogin: false,
    enableWindowsAdLogin: false,
    enableYufuLogin: false,
    enableSensitiveWord: false,
    enableGlobalFullTextSearch: false,
    allowChangeNickname: true,
    enableViewAllOrgUser: true,
    enableManageAntivirus: false,
    ssoWay: '',
    syncWay: '',
    showWeworkAppLink: false,
    defaultUserOptions: initUserOptions,
    defaultTeamOptions: initTeamOptions,
    expireTime: '',
    totalFileSize: '0',
    // 剩余配额 = 网盘总配额 - 已分配的配额
    remainingSpaceQuota: '0',
    domains: [],
    teamSpaceQuota: '',
    groupSpaceQuota: '',
    allowWeworkSelfApp: false,
    defaultLogo,
    userCountInfo: {
      total: 0,
      used: 0,
      rest: 0,
      temporaryUser: 0,
      online: 0,
      disabledFormalUser: 0,
      disabledTemporaryUser: 0,
    },
    instanceId: '',
    supportProtocols: [],
    isAccountNotDependentOnPhone: false,
    corpList: [],
  },
  mutations: {
    SET_ORG_BUSINESS: (state: OrgState, bussiness): void => {
      state.bussiness = bussiness;
    },
    SET_ORG_NAME: (state: OrgState, name: string): void => {
      state.orgName = name;
    },
    SET_ORG_LOGO: (state: OrgState, logo: string): void => {
      state.orgLogo = logo;
    },
    SET_ENABLE_ANTIVIRUS: (state: OrgState, enableAntivirus: boolean): void => {
      state.enableAntivirus = enableAntivirus;
    },
    SET_SHOW_ORG_NAME: (state: OrgState, showOrgName: boolean): void => {
      state.showOrgName = showOrgName;
    },
    SET_ORG_SPACE: (state: OrgState, space: string): void => {
      state.space = space;
    },
    SET_ENABLE_MANAGE_WATERMARK: (state: OrgState, enableManageWatermark: boolean): void => {
      state.enableManageWatermark = enableManageWatermark;
    },
    SET_PREVIEW_WATERMARK_CONFIG: (state: OrgState, previewWatermarkConfig: CoFile.PreviewWatermarkConfig): void => {
      state.previewWatermarkConfig = previewWatermarkConfig;
    },
    SET_DOWNLOAD_WATERMARK_CONFIG: (state: OrgState, downloadWatermarkConfig: CoFile.DownloadWatermarkConfig): void => {
      state.downloadWatermarkConfig = downloadWatermarkConfig;
    },
    SET_SHARE_WATERMARK_CONFIG: (state: OrgState, shareWatermarkConfig: CoFile.ShareWatermarkConfig): void => {
      state.shareWatermarkConfig = shareWatermarkConfig;
    },
    SET_ENABLE_SHARE: (state: OrgState, enableShare: boolean): void => {
      state.enableShare = enableShare;
    },
    SET_ENABLE_WEWORK: (state: OrgState, enableWeworkLogin: boolean): void => {
      state.enableWeworkLogin = enableWeworkLogin;
    },
    SET_ENABLE_OPEN_LDAP: (state: OrgState, enableOpenLdapLogin: boolean): void => {
      state.enableOpenLdapLogin = enableOpenLdapLogin;
    },
    SET_ENABLE_WINDOWS_AD: (state: OrgState, enableWindowsAdLogin: boolean): void => {
      state.enableWindowsAdLogin = enableWindowsAdLogin;
    },
    SET_ENABLE_YUFU: (state: OrgState, enableYufuLogin: boolean): void => {
      state.enableYufuLogin = enableYufuLogin;
    },
    SET_ENABLE_SENSITIVEWORD: (state: OrgState, enableSensitiveWord: boolean): void => {
      state.enableSensitiveWord = enableSensitiveWord;
    },
    SET_ENABLE_GLOBAL_FULLTEXTSEARCH: (state: OrgState, enableGlobalFullTextSearch: boolean): void => {
      state.enableGlobalFullTextSearch = enableGlobalFullTextSearch;
    },
    SET_ENABLE_MANAGEANTIVIRUS: (state: OrgState, enableManageAntivirus: boolean): void => {
      state.enableManageAntivirus = enableManageAntivirus;
    },
    SET_ORG_SSO_WAY: (state: OrgState, ssoWay: string): void => {
      state.ssoWay = ssoWay;
    },
    SET_ORG_SYNC_WAY: (state: OrgState, syncWay: string): void => {
      state.syncWay = syncWay;
    },
    SET_SHOW_WEWORK_APP_LINK: (state: OrgState, showWeworkAppLink: boolean): void => {
      state.showWeworkAppLink = showWeworkAppLink;
    },
    SET_VIEW_ALL_ORG_USER: (state: OrgState, enableViewAllOrgUser: boolean): void => {
      state.enableViewAllOrgUser = enableViewAllOrgUser;
    },
    SET_ALLOW_CHANGE_NICKNAME: (state: OrgState, allowChangeNickname: boolean): void => {
      state.allowChangeNickname = allowChangeNickname;
    },
    SET_USER_OPTIONS: (state: OrgState, userOptions: SMH.DefaultUserOptions): void => {
      state.defaultUserOptions = userOptions;
    },
    SET_TEAM_OPTIONS: (state: OrgState, teamOptions: SMH.DefaultTeamOptions): void => {
      state.defaultTeamOptions = teamOptions;
    },
    SET_EXPIRE_TIME: (state: OrgState, expireTime: string): void => {
      state.expireTime = expireTime;
    },
    SET_TOTAL_FILE_SIZE: (state: OrgState, totalFileSize: string): void => {
      state.totalFileSize = totalFileSize;
    },
    SET_REMAINING_QUOTA: (state: OrgState, remainingSpaceQuota: string): void => {
      state.remainingSpaceQuota = remainingSpaceQuota;
    },
    SET_DOMAINS: (state: OrgState, domains: SMH.DomainItem[]): void => {
      state.domains = domains;
    },
    SET_TEAM_SPACE_QUOTA: (state: OrgState, teamSpaceQuota: string): void => {
      state.teamSpaceQuota = teamSpaceQuota;
    },
    SET_GROUP_SPACE_QUOTA: (state: OrgState, groupSpaceQuota: string): void => {
      state.groupSpaceQuota = groupSpaceQuota;
    },
    SET_ALLOW_WEWORK_SELF: (state: OrgState, allowWeworkSelfApp: boolean): void => {
      state.allowWeworkSelfApp = allowWeworkSelfApp;
    },
    SET_USER_COUNT_INFO: (state: OrgState, userCountInfo: UserCountInfo): void => {
      state.userCountInfo = userCountInfo;
    },
    SET_INSTANCE_ID: (state: OrgState, instanceId: string): void => {
      state.instanceId = instanceId;
    },
    SET_SUPPORT_PROTOCOLS: (state: OrgState, supportProtocols: string[]): void => {
      state.supportProtocols = supportProtocols;
    },
    SET_NOTDEPENDENT_PHONE: (state: OrgState, isAccountNotDependentOnPhone: boolean): void => {
      state.isAccountNotDependentOnPhone = isAccountNotDependentOnPhone;
    },
    SET_CORP_LIST: (state: OrgState, corpList: CorpItem[]): void => {
      state.corpList = corpList;
    },
  },
  getters: {
    bussiness: (state: OrgState) => state.bussiness,
    orgName: (state: OrgState) => state.orgName,
    orgLogo: (state: OrgState) => state.orgLogo,
    enableAntivirus: (state: OrgState) => state.enableAntivirus,
    showOrgName: (state: OrgState) => state.showOrgName,
    orgSpace: (state: OrgState) => state.space,
    totalFileSize: (state: OrgState) => state.totalFileSize,
    enableManageWatermark: (state: OrgState) => state.enableManageWatermark,
    previewWatermarkConfig: (state: OrgState) => state.previewWatermarkConfig,
    downloadWatermarkConfig: (state: OrgState) => state.downloadWatermarkConfig,
    shareWatermarkConfig: (state: OrgState) => state.shareWatermarkConfig,
    remainingSpaceQuota: (state: OrgState) => state.remainingSpaceQuota,
    enableShare: (state: OrgState) => state.enableShare,
    enableWeworkLogin: (state: OrgState) => state.enableWeworkLogin,
    enableYufuLogin: (state: OrgState) => state.enableYufuLogin,
    enableOpenLdapLogin: (state: OrgState) => state.enableOpenLdapLogin,
    enableViewAllOrgUser: (state: OrgState) => state.enableViewAllOrgUser,
    allowChangeNickname: (state: OrgState) => state.allowChangeNickname,
    enableWindowsAdLogin: (state: OrgState) => state.enableWindowsAdLogin,
    enableSensitiveWord: (state: OrgState) => state.enableSensitiveWord,
    enableGlobalFullTextSearch: (state: OrgState) => state.enableGlobalFullTextSearch,
    enableManageAntivirus: (state: OrgState) => state.enableManageAntivirus,
    ssoWay: (state: OrgState) => state.ssoWay,
    syncWay: (state: OrgState) => state.syncWay,
    showWeworkAppLink: (state: OrgState) => state.showWeworkAppLink,
    defaultUserOptions: (state: OrgState) => state.defaultUserOptions,
    expireTime: (state: OrgState) => state.expireTime,
    domains: (state: OrgState) => state.domains,
    teamSpaceQuota: (state: OrgState) => state.teamSpaceQuota,
    groupSpaceQuota: (state: OrgState) => state.groupSpaceQuota,
    allowWeworkSelfApp: (state: OrgState) => state.allowWeworkSelfApp,
    defaultLogo: (state: OrgState) => (IS_PRIVATE ? httpUrl.staticCDNURLMap.productLogoUrl : state.defaultLogo),
    userCountInfo: (state: OrgState) => state.userCountInfo,
    instanceId: (state: OrgState) => state.instanceId,
    supportProtocols: (state: OrgState) => state.supportProtocols,
    isAccountNotDependentOnPhone: (state: OrgState) => state.isAccountNotDependentOnPhone,
    corpList: (state: OrgState) => state.corpList,
  },
  actions: {
    // 获取当前账号所处企业，是否开启云查杀
    async getEnableVirus(
      {
        commit,
      }: {
        commit: Commit;
      },
      organizationId: number,
    ): Promise<void> {
      try {
        const { data } = await smh.userAPI.getSecurityScanConfig();
        const { enableAntivirus } = data;
        commit('SET_ENABLE_ANTIVIRUS', enableAntivirus);
      } catch (error: MsgError) {
        tips.error(error);
      }
    },
    // 获取企业基本信息
    async GetOrgInfo(
      {
        commit,
        dispatch,
      }: {
        commit: Commit;
        dispatch: Dispatch;
      },
      _organizationId?: number,
    ): Promise<void> {
      // 其他地方暂时不需要调用获取安全扫描开关
      // dispatch('getEnableVirus');
      try {
        const organizationId = _organizationId ?? (user.getUserInfo().organizationId || 0);
        const { data } = await smh.userAPI.getOrgInfo({
          organizationId,
        });
        const { expired, id, name, extensionData = null, domains = [], bussiness } = data;
        const defaultLogoUrl = IS_PRIVATE ? httpUrl.staticCDNURLMap.productLogoUrl : defaultLogo;
        const logo = extensionData?.logo ? `${httpUrl.protocol}:${extensionData.logo}` : defaultLogoUrl;
        const editionConfig = extensionData?.editionConfig || {
          editionFlag: 'enterprise',
        };
        const enableManageWatermark = extensionData?.editionConfig?.enableManageWatermark ?? true;
        const showOrgName = extensionData?.showOrgNameInUI || false;
        const watermarkOptions = extensionData?.watermarkOptions || null;
        const previewWatermarkConfig = {
          enablePreviewWatermark: watermarkOptions?.enablePreviewWatermark || false,
          previewWatermarkType: watermarkOptions?.previewWatermarkType || 0,
        };
        const downloadWatermarkConfig = {
          enableDownloadWatermark: watermarkOptions?.enableDownloadWatermark || false,
          downloadWatermarkType: watermarkOptions?.downloadWatermarkType || 0,
        };
        const shareWatermarkConfig = {
          enableShareWatermark: watermarkOptions?.enableShareWatermark || false,
          shareWatermarkType: watermarkOptions?.shareWatermarkType ?? 1,
        };
        const enableShare = extensionData?.enableShare || editionConfig.editionFlag === 'personal' || false;
        const enableOpenLdapLogin = data?.extensionData?.enableOpenLdapLogin || false;
        const enableWindowsAdLogin = data?.extensionData?.enableWindowsAdLogin || false;
        const enableWeworkLogin = data?.extensionData?.enableWeworkLogin || false;
        const enableViewAllOrgUser =
          typeof data?.extensionData?.enableViewAllOrgUser === 'boolean'
            ? data?.extensionData?.enableViewAllOrgUser
            : true;
        const allowChangeNickname =
          typeof data?.extensionData?.allowChangeNickname === 'boolean'
            ? data?.extensionData?.allowChangeNickname
            : true;
        const enableYufuLogin = data?.extensionData?.enableYufuLogin || false;
        const enableSensitiveWord = data?.extensionData?.enableSensitiveWord || false;
        const enableGlobalFullTextSearch = data?.extensionData?.enableGlobalFullTextSearch || false;
        const enableManageAntivirus = data?.extensionData?.editionConfig?.enableManageAntivirus || false;
        const ssoWay = data?.extensionData?.ssoWay || '';
        const syncWay = data?.extensionData?.syncWay || '';
        const showWeworkAppLink = data?.extensionData?.showWeworkAppLink || false;
        const userOptions = extensionData?.defaultUserOptions || initUserOptions;
        const teamOptions = extensionData?.defaultTeamOptions || initTeamOptions;
        const expireTime = extensionData?.expireTime && dayjs(extensionData?.expireTime).format('YYYY-MM-DD HH:mm:ss');
        const allowWeworkSelfApp = extensionData?.allowWeworkSelfApp || false;
        const channelFlag = extensionData?.channelFlag || '';
        const channelUpdated = extensionData?.isUpdated || false;
        const instanceId = domains.length > 0 ? domains[0].domain : CONSTANT_URL.currentDomain;
        const isAccountNotDependentOnPhone = data?.extensionData?.isAccountNotDependentOnPhone || false;
        commit('SET_ORG_BUSINESS', bussiness);
        commit('SET_ORG_NAME', name);
        commit('SET_ORG_LOGO', logo);
        commit('SET_SHOW_ORG_NAME', showOrgName);
        commit('SET_ENABLE_MANAGE_WATERMARK', enableManageWatermark);
        commit('SET_PREVIEW_WATERMARK_CONFIG', previewWatermarkConfig);
        commit('SET_DOWNLOAD_WATERMARK_CONFIG', downloadWatermarkConfig);
        commit('SET_SHARE_WATERMARK_CONFIG', shareWatermarkConfig);
        commit('SET_ENABLE_SHARE', enableShare);
        commit('SET_ENABLE_OPEN_LDAP', enableOpenLdapLogin);
        commit('SET_ENABLE_WINDOWS_AD', enableWindowsAdLogin);
        commit('SET_ENABLE_WEWORK', enableWeworkLogin);
        commit('SET_ENABLE_YUFU', enableYufuLogin);
        commit('SET_VIEW_ALL_ORG_USER', enableViewAllOrgUser);
        commit('SET_ALLOW_CHANGE_NICKNAME', allowChangeNickname);
        commit('SET_ENABLE_SENSITIVEWORD', enableSensitiveWord);
        commit('SET_ENABLE_GLOBAL_FULLTEXTSEARCH', enableGlobalFullTextSearch);
        commit('SET_ENABLE_MANAGEANTIVIRUS', enableManageAntivirus);
        commit('SET_ORG_SSO_WAY', ssoWay);
        commit('SET_ORG_SYNC_WAY', syncWay);
        commit('SET_SHOW_WEWORK_APP_LINK', showWeworkAppLink);
        commit('SET_USER_OPTIONS', userOptions);
        commit('SET_TEAM_OPTIONS', teamOptions);
        commit('SET_EXPIRE_TIME', expireTime);
        commit('SET_DOMAINS', domains);
        commit('SET_ALLOW_WEWORK_SELF', allowWeworkSelfApp);
        commit('SET_INSTANCE_ID', instanceId.split('.')[0]);
        commit('SET_NOTDEPENDENT_PHONE', isAccountNotDependentOnPhone);
        user.setOrgExpired(expired);
        user.setOrgChannel(channelFlag);
        user.setChannelUpdated(channelUpdated);
      } catch (error) {
        commit('SET_ORG_NAME', meeting.isTMWebview ? i18n_t('会议文件空间') : i18n_t('腾讯云企业网盘'));
        commit('SET_ORG_LOGO', defaultLogo);
        commit('SET_VIEW_ALL_ORG_USER', true);
        commit('SET_ALLOW_CHANGE_NICKNAME', true);
        commit('SET_SHOW_ORG_NAME', false);
        commit('SET_ENABLE_SHARE', false);
        commit('SET_ENABLE_OPEN_LDAP', false);
        commit('SET_ENABLE_WINDOWS_AD', false);
        commit('SET_ENABLE_WEWORK', false);
        commit('SET_ENABLE_YUFU', false);
        commit('SET_ENABLE_SENSITIVEWORD', false);
        commit('SET_ENABLE_GLOBAL_FULLTEXTSEARCH', false);
        commit('SET_ENABLE_MANAGEANTIVIRUS', false);
        commit('SET_ORG_SSO_WAY', '');
        commit('SET_ORG_SYNC_WAY', '');
        commit('SET_USER_OPTIONS', initUserOptions);
        commit('SET_TEAM_OPTIONS', initTeamOptions);
        commit('SET_EXPIRE_TIME', '');
        commit('SET_DOMAINS', []);
        commit('SET_ENABLE_MANAGE_WATERMARK', true);
        commit('SET_PREVIEW_WATERMARK_CONFIG', previewWatermarkOptions);
        commit('SET_DOWNLOAD_WATERMARK_CONFIG', downloadWatermarkOptions);
        commit('SET_SHARE_WATERMARK_CONFIG', shareWatermarkOptions);
        commit('SET_NOTDEPENDENT_PHONE', false);
      }
    },
    // 获取企业配额
    async GetOrgSpace({ commit }: { commit: Commit }): Promise<SpaceInfo> {
      let resCapacity = '0'; // 返回企业总配额
      let resAvailableSpace = '0'; // 返回企业剩余可分配的配额
      let resTotalAllocatedSpaceQuota = '0'; // 返回企业已分配的配额
      let resTotalFileSize = '0';
      const fakeSpace = 999 * 1024 * 1024 * 1024; // 前端兜底999GB，允许用户设置，后端会校验
      try {
        const { data } = await smh.userAPI.getOrgSpace();
        const { capacity, availableSpace, totalAllocatedSpaceQuota, totalFileSize } = data;
        resCapacity = capacity || '0';
        resAvailableSpace = availableSpace || `${fakeSpace}`;
        resTotalAllocatedSpaceQuota = totalAllocatedSpaceQuota || '0';
        resTotalFileSize = totalFileSize || '0';
        commit('SET_ORG_SPACE', `${resCapacity}`);
        commit('SET_REMAINING_QUOTA', `${availableSpace || '0'}`);
        commit('SET_TOTAL_FILE_SIZE', `${totalFileSize}`);
      } catch (error) {
        commit('SET_ORG_SPACE', '0');
        commit('SET_REMAINING_QUOTA', '0');
        commit('SET_TOTAL_FILE_SIZE', '0');
      }
      return {
        capacity: resCapacity,
        availableSpace: resAvailableSpace,
        totalAllocatedSpaceQuota: resTotalAllocatedSpaceQuota,
      };
    },
    // 获取企业用户数
    async getUserCount({ commit }: { commit: Commit }): Promise<void> {
      try {
        const { data } = await smh.userAPI.getUserCount();
        const userLimit = data?.userLimit || 0;
        const currentUser = data?.currentUser || 0;
        const temporaryUser = data?.temporaryUser || 0;
        const remainCount = userLimit - currentUser - temporaryUser;
        const onlineCount = data?.onlineUser || 0;
        const disabledFormalUser = data?.disabledFormalUser || 0;
        const disabledTemporaryUser = data?.disabledTemporaryUser || 0;
        const userCountInfo = {
          total: userLimit,
          used: currentUser,
          rest: Math.max(0, remainCount),
          temporaryUser,
          disabledFormalUser,
          disabledTemporaryUser,
          online: onlineCount,
        };
        commit('SET_USER_COUNT_INFO', userCountInfo);
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    async checkUserCount({ state, dispatch }: { state: OrgState; dispatch: Dispatch }) {
      await dispatch('getUserCount');
      const { rest } = state.userCountInfo;
      if (rest === 0) {
        tips.warning(i18n_t('用户已达上限'));
        return false;
      }
      return true;
    },
    // 获取当前企业支持的配置
    async getSupportProtocolList({ commit }: { commit: Commit }) {
      try {
        const res = await smh.userAPI.getSupportProtocolList();
        const supportProtocols = res.data || [];
        commit('SET_SUPPORT_PROTOCOLS', supportProtocols);
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 私有化下获取企业列表
    async getCorpList({ commit }: { commit: Commit }) {
      try {
        const { data } = await smh.api.getCorpList();
        const list = data.organizationList;
        commit('SET_CORP_LIST', list);
      } catch (e: any) {
        commit('SET_CORP_LIST', []);
      }
    },
  },
};
export default org;
